import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, Observable, Subject, throwError } from "rxjs";
import { INegotiationModel } from "src/app/components/external-dashboards/quotation-table/quote.model";
import { BackendListModel } from "src/app/models/common/list.model";
import { ReqMenuItem } from "src/app/req-menu-item";
import { ApiRoutes } from "../apiroutes";
import { TermsAndConditionsModel } from "src/app/models/common/terms-and-conditions.model";
import { LeaseAgreement, RequestDetailModel } from "src/app/models/request-detail-model";
import { PickupDropoff } from "src/app/models/pickup-dropoff";
import { LeaseDocumentTypes, LeaseQuoteModel, LeaseRemarks } from "src/app/models/lease-quote";
import {
    DropoffSchedule,
    PickupSchedule
} from "src/app/components/external-dashboards/quoteTable/lease-quote-table/lease-quote";
import { environment } from "../../../environments/environment";

@Injectable({
    providedIn: 'root'
})

export class RequestApiService {
    base_url = ApiRoutes.LeadRequest;
    booking_url = ApiRoutes.Booking;
    container_url = ApiRoutes.ContainerMaster;
    bookingCharge_url = ApiRoutes.BookingCharge;
    preference_url = ApiRoutes.Core;

    selectedRequest: RequestDetailModel | any;
    requirementList: [] = [];
    selectedRequestRequirements;
    reviewQuotation;
    selectedAgreementDetails;
    confirmBooking: boolean = false;
    doc_url = ApiRoutes.DocumentType;
    leaseSubject = new Subject<LeaseAgreement>();
    leaseRequestSubject = new Subject<LeaseQuoteModel>();
    leaseRequestCurrency = new Subject<any>();
    pickupScheduleListSubject = new Subject<PickupSchedule[]>();
    dropoffScheduleListSubject = new Subject<DropoffSchedule[]>();
    bookingDetailsExpand: boolean = true;

    constructor(private http: HttpClient) {
    }

    postRequest(data) {
        const url = `${this.base_url}request_details/nested/`;
        return this.http.post(url, data);
    }

    patchRequest(id, data) {
        const url = `${this.base_url}request_details/${id}/`;
        return this.http.patch(url, data);
    }

    updateRequestLocations(id, data) {
        const url = `${this.base_url}request_details/${id}/locations/`;
        return this.http.put(url, data);
    }

    postRequestRequirements(data) {
        const url = `${this.base_url}requirements/`;
        return this.http.post(url, data);
    }

    updateRequestRequirements(id, data) {
        const url = `${this.base_url}requirements/${id}/`;
        return this.http.put(url, data);
    }

    postLeaseAgreement(reqId, data: LeaseAgreement) {
        const url = `${this.base_url}${reqId}/lease_agreement/`;
        return this.http.post(url, data);
    }

    updateLeaseAgreement(reqId, data: LeaseAgreement) {
        const url = `${this.base_url}${reqId}/lease_agreement/${data.id}/`;
        return this.http.put(url, data);
    }

    updateLeaseAgreementDocAndTnc(reqId, data) {
        const url = `${this.base_url}${reqId}/lease_agreement_tnc_documents/${data.id}/`;
        return this.http.put(url, data);
    }

    optionsLeaseAgreement(reqId) {
        const url = `${this.base_url}${reqId}/lease_agreement/`;
        return this.http.options(url);
    }

    // Get first quote
    getFirstQuote(request): Observable<BackendListModel<any>> {
        let url = `${this.base_url}request_details/${request.id}/first_quote/`;
        url = `${url}${this.getUrlByRequestType(request.request_type.dev_name)}`;

        return this.http.get<BackendListModel<any>>(url);
    }

    // Get last quote
    getlastQuote(request): Observable<BackendListModel<INegotiationModel>> {
        let url = `${this.base_url}request_quotes/${request?.id}/last_quote/`;
        url = `${url}${this.getUrlByRequestType(request?.request_type.dev_name)}`;
        return this.http.get<any>(url);
    }

    getBaseQuote(request) {
        let url = `${this.base_url}request_quotes/${request?.id}/last_quote/`;
        url = `${url}${this.getUrlByRequestType(request?.request_type.dev_name)}`;
        return this.http.get<any>(url);
    }

    // GET request details by ID
    getRequestDetailsById(requestId): Observable<any> {
        let url = `${this.base_url}request_details/${requestId}/`;
        return this.http.get<BackendListModel<any>>(url);
    }


    // Mark as read
    markAsRead(request) {
        let data = {}
        let url = `${this.base_url}request_details/${request?.id}/mark_as_read/`;

        return this.http.put(url, data);
    }

    // POST Quotation
    postQuotations(quote, request = this.selectedRequest) {
        let url = `${this.base_url}request_quotes/`;
        url = `${url}${this.getUrlByRequestType(request.request_type.dev_name)}`;
        return this.http.post(url, quote);
    }

    postQuote(quote, requirementId) {
        if (requirementId == null) {
            return;
        }
        let url = `${this.base_url}request_quotes/${requirementId}/`;
        url = `${url}${this.getUrlByRequestType(this.selectedRequest.request_type.dev_name)}`;

        return this.http.post(url, quote);
    }

    // GET quote history
    getQuoteHistory(requirementId): Observable<BackendListModel<INegotiationModel>> {
        if (requirementId == null) {
            return;
        }
        let url = `${this.base_url}request_quotes/${requirementId}/quote_history/`;
        url = `${url}${this.getUrlByRequestType(this.selectedRequest.request_type.dev_name)}`;

        return this.http.get<BackendListModel<INegotiationModel>>(url);
    }

    // POST Quote after Request created by Internal User
    postQuoteAfterRequestCreation(request, quote) {
        if (request.requirement[0].id == null) {
            return;
        }
        let url = `${this.base_url}request_quotes/${request.requirement[0].id}/`;
        url = `${url}${this.getUrlByRequestType(request.request_type.dev_name)}`;

        return this.http.post(url, quote);
    }

    // GET Request Menu Items
    getRequestMenuItems(requestId): Observable<Array<ReqMenuItem>> {
        let url = `${this.base_url}menu-items/?request_id=${requestId}`;
        return this.http.get<Array<any>>(url);
    }

    // GET Request Documents
    getRequestDocumentsById(requestId) {
        let url = `${this.base_url}request_documents/?request_id=${requestId}`;
        return this.http.get(url);
    }

    // POST Request Documents
    postDocuments(data) {
        let url = `${this.base_url}request_documents/`;
        return this.http.post(url, data);
    }

    // URL switching function w.r.t request_type.dev_name
    getUrlByRequestType(requestType) {
        switch (requestType) {
            case 'one_way':
                return `oneway/`
            case 'buy_sell':
                return `trade/`
            case 'lease':
                return `lease/`
            case 'empty_repo':
                return `empty_repo/`
        }
    }

    getGroupingByStatus(data) {
        let url = `${this.base_url}status_based_groupings/`;
        let urlObj = new URL(url);

        if (data.request_age_in_days != null) {
            urlObj.searchParams.append('request_age_in_days', data.request_age_in_days);
        }
        if (data.request_type != null && data.request_type != '') {
            urlObj.searchParams.append('request_type', data.request_type);
        }

        return this.http.get<Array<any>>(urlObj.toString());
    }

    getGroupingByStatusByCreatedUser(data) {
        let url = `${this.base_url}status_based_groupings_for_created_users/`;
        let urlObj = new URL(url);

        if (data.request_age_in_days != null) {
            urlObj.searchParams.append('request_age_in_days', data.request_age_in_days);
        }
        if (data.request_type != null && data.request_type != '') {
            urlObj.searchParams.append('request_type', data.request_type);
        }
        return this.http.get<Array<any>>(urlObj.toString());
    }

    getCommercials(requestId: string, type: string) {
        let url = `${this.base_url}request_quotes/${requestId}/last_quote/${type}/`;
        return this.http.get<any>(url);
    }

    getDepotList(url: string, id: string, rId: string) {
        return this.http.get<BackendListModel<INegotiationModel>>(url + 'location_short/?search=&depot_owner_type=' + id + '&pickup_request_id=' + rId);
    }

    getTermsAndConditionsList(request, group): Observable<BackendListModel<TermsAndConditionsModel>> {
        let url = `${this.base_url}terms_condition/`;
        let urlObj = new URL(url);
        // urlObj.searchParams.append('page_size', String(page_size));
        // urlObj.searchParams.append('page', String(page));
        urlObj.searchParams.append('request_type', String(request.request_type.dev_name));
        urlObj.searchParams.append('transaction_type', String(request.transaction_type));
        urlObj.searchParams.append('group', String(group));
        return this.http.get<BackendListModel<TermsAndConditionsModel>>(String(urlObj));
    }

    getPickDropoffUpScheduleList(requestId: string) {
        return this.http.get<BackendListModel<PickupDropoff>>(this.base_url + requestId + '/lease_schedule_by_requirement/');
    }

    getTermsAndConditionsListLease(request): Observable<BackendListModel<TermsAndConditionsModel>> {
        let url = `${this.base_url}terms_condition/`;
        let urlObj = new URL(url);
        urlObj.searchParams.append('request_type', String(request.request_type.dev_name));
        urlObj.searchParams.append('transaction_type', String(request.transaction_type));
        urlObj.searchParams.append('group', 'quote');
        return this.http.get<BackendListModel<TermsAndConditionsModel>>(String(urlObj));
    }

    getTermsAndConditionsListDO(request, type): Observable<BackendListModel<TermsAndConditionsModel>> {
        let url = `${this.base_url}terms_condition/`;
        let urlObj = new URL(url);
        urlObj.searchParams.append('request_type', String(request.request_type.dev_name));
        urlObj.searchParams.append('transaction_type', String(request.transaction_type));
        urlObj.searchParams.append('group', type);
        return this.http.get<BackendListModel<TermsAndConditionsModel>>(String(urlObj));
    }

    getDocumentTypeList(grp: string) {
        let url = `${this.doc_url}?group=${grp}`;
        return this.http.get<LeaseDocumentTypes>(url);

    }

    getRemarksList(requestId: string) {
        return this.http.get<LeaseRemarks>(this.base_url + requestId + '/request_remarks/');
    }

    postRequestRemarks(requestId: string, remark: string, isInternalFlag: boolean) {
        return this.http.post(this.base_url + requestId + '/request_remarks/', {
            request: requestId,
            remarks: remark,
            is_internal: isInternalFlag,
        });
    }

    getPenaltyTypes(requestId) {
        const url = `${this.base_url}${requestId}/lease_quote_ero/`;
        return this.http.options(url);
    }

    updateLeaseSubjectValue(lease): void {
        this.leaseSubject.next(lease);
    }

    updateleaseRequestSubjectValue(leaseReq): void {
        this.leaseRequestSubject.next(leaseReq);
    }

    updateleaseRequestCurrency(leaseCurrency): void {
        this.leaseRequestCurrency.next(leaseCurrency);
    }

    confirmLeaseBooking(quote) {
        let url = `${this.base_url}request_status/${this.selectedRequest.id}/booking_confirmation/`;
        return this.http.post(url, quote);
    }

    confirmEmptyRepoBooking(quote) {
        let url = `${this.base_url}request_status/${this.selectedRequest.id}/booking_confirmation/`;
        return this.http.post(url, quote);
    }

    getStorageChargebyRequirementId(reqId) {
        let url = `${this.base_url}request_quotes/${reqId}/last_quote_item/`;
        url = `${url}${this.getUrlByRequestType(this.selectedRequest?.request_type.dev_name)}`;
        return this.http.get<any>(url);
    }

    getRequestAllocationListById(reqId, page_size = 10, page = 1) {
        let url = new URL(`${this.base_url}request_user_allocation_details/`);
        url.searchParams.append('request_id', String(reqId));
        url.searchParams.append('page_size', String(page_size));
        url.searchParams.append('page', String(page));
        return this.http.get<any>(url.toString());
    }

    getQuoteContentForEmail(quoteId, type, mailType) {
        let url = ''
        if (mailType == 'quote')
            url = `${this.base_url}request_quotes/email/${type}/${quoteId}/`;
        else
            url = `${this.base_url}request_quotes/email/booking_${type}/${quoteId}/`;
        return this.http.get(url, { responseType: 'text' });
    }

    getEmailContent(id, mailType) {
        let url = ''
        if (mailType == 'releaseOrder')
            url = `${this.booking_url}do_details_mail/${id}/`;
        else if (mailType == 'returnOrder')
            url = `${this.booking_url}rr_details_mail/${id}/`;
        return this.http.get(url, { responseType: 'text' });
    }

    getRequestQuoteCharges(serviceType: string) {
        let url = `${this.base_url}request_quotes/charges/?service_type=${serviceType}`;
        return this.http.get<any>(url);
    }

    getContainersListForReexport(requestId: string) {
        let url = `${environment.baseApiUrl}/api/booking/pickup_based_return_container_reference/` + requestId + '/';
        return this.http.get<[]>(url);
    }

    getReexportedContainersList(requestId: string) {
        let url = `${this.container_url}reexport_details/` + requestId + '/';
        return this.http.get<[]>(url);
    }

    postReexportedContainersList(requestId: string, containerDetails: any) {
        let url = `${this.container_url}reexport_details/` + requestId + '/';
        return this.http.post<[]>(url, containerDetails);
    }

    getBookingDetailsById(booking): Observable<any> {
        let url = `${this.bookingCharge_url}booking/${booking}/`;
        return this.http.get<BackendListModel<any>>(url);
    }

    getBookingChargeDetailsById(booking): Observable<any> {
        let url = `${this.bookingCharge_url}booking_charges/?booking=${booking}`;
        return this.http.get<BackendListModel<any>>(url);
    }

    getAllRequirementsAndContainers(requestId: string) {
        let url = `${this.base_url}requirement_containers/?request=${requestId}`;
        return this.http.get<BackendListModel<any>>(url);
    }

    postOpsDateChanges(data) {
        const url = `${this.booking_url}grouped_pickup_dropoff_approval/`;
        return this.http.post(url, data);
    }

    getOpsDateChangesList(data) {
        const url = `${this.booking_url}grouped_pickup_dropoff_approval/${data}/`;
        return this.http.get(url, data);
    }

    getOpsDataOfRequest(id) {
        const url = `${this.booking_url}grouped_pickup_dropoff_approval/?request_id=${id}`;
        return this.http.get(url);
    }

    getMovementDetails(data: any) {
        const url = `${this.bookingCharge_url}booking/${data}/`;
        return this.http.get(url, data);
    }

    postMovementDetails(data: any) {
        const url = `${this.bookingCharge_url}booking/${data.booking}/`;
        return this.http.put(url, data);
    }

    postQuantityAndLocations(id: string, data: any) {
        const url = `${this.bookingCharge_url}booking_requirement/${id}/`;
        return this.http.put(url, data);
    }

    postBookingCharges(data): Observable<any> {
        let url = `${this.bookingCharge_url}booking_charges/`;
        return this.http.post<any>(url, data).pipe(
            catchError((error) => {
                return throwError(() => new Error(error));
            })
        );
    }

    updateRequirement(bookingCharge: string, data: any) {
        let url = `${this.bookingCharge_url}booking_charges/${bookingCharge}/`;
        return this.http.put<any>(url, data).pipe(
            catchError((error) => {
                return throwError(() => new Error(error));
            })
        );
    }

    deleteRequirement(bookingCharge: string, data: any) {
        let url = `${this.bookingCharge_url}booking_charges/${bookingCharge}/`;
        return this.http.delete<any>(url, data).pipe(
            catchError((error) => {
                return throwError(() => new Error(error));
            })
        );
    }

    getRentalDetails(booking: string, toDate: string) {
        let url = `${this.bookingCharge_url}calculate_per_diem_charge/`;
        return this.http.post(url, {
            booking: booking,
            to_date: toDate
        });
    }

    getInvoiceTypesFromArthakosh() {
        const url = `${this.bookingCharge_url}arthakosh_invoice_type/`;
        return this.http.get(url);
    }

    getGlobalPreferences() {
        const url = `${this.preference_url}global_preferences/`;
        return this.http.get(url);
    }

    postReopenLog(data: any) {
        const url = `${this.bookingCharge_url}booking_reopen_log/`;
        return this.http.post(url, data);
    }

    // getReopenBookingLog() {
    //     const url = `${this.bookingCharge_url}booking_reopen_log/`;
    //     return this.http.get(url);
    // }

    getReopenBookingLog(searchTerm = null, page_size = 10, page = 1, order = '') {
        let url = `${this.bookingCharge_url}booking_reopen_log/?booking=${ this.selectedRequest?.booking}`;
        let urlObj = new URL(url);
        if (searchTerm) {
            urlObj.searchParams.append('search', searchTerm);
        }
        urlObj.searchParams.append('page_size', String(page_size));
        urlObj.searchParams.append('page', String(page));
        if (order != null && order != '') {
            urlObj.searchParams.append('o', String(order));
        }
        return this.http.get<BackendListModel<any>>(urlObj.toString());
    }

    postInvoiceParty(data: any) {
        const url = `${this.bookingCharge_url}booking_stakeholder/`;
        return this.http.post(url, data);
    }

    updateBookingQuantity(id: string, data: any) {
        const url = `${this.bookingCharge_url}update_quantity/${id}/`;
        return this.http.put(url, data);
    }

    updateContainerSizeType(id: string, data: any) {
        const url = `${this.bookingCharge_url}update_size_type/${id}/`;
        return this.http.put(url, data);
    }

    updateBookingLocation(id: string, data: any) {
        const url = `${this.bookingCharge_url}update_booking_locations/${id}/`;
        return this.http.put(url, data);
    }

    updateOnewayfreedaysPerdiem(id: string, data: any) {
        const url = `${this.bookingCharge_url}update_one_way_free_days_and_per_diem/${id}/`;
        return this.http.put(url, data);
    }

    updateLeasingPeriodAndPerDiem(id: string, data: any) {
        const url = `${this.bookingCharge_url}update_leasing_period_days_and_per_diem_charge/${id}/`;
        return this.http.put(url, data);
    }

    updateBuildDownPeriodAndCharges(id: string, data: any) {
        const url = `${this.bookingCharge_url}update_build_down_period_days_and_post_build_down_per_diem_charge/${id}/`;
        return this.http.put(url, data);
    }
}
